import { EMPTY_STRING } from '@rotomeca/utils';
import { HTMLBnumInput } from '../input/bnum-input';
import { EVENT_DEFAULT } from '../../../core/utils/constants/constants';
import { BnumConfig } from '../../../core/utils/BnumConfig';
import { HTMLBnumButtonIcon } from '../icon-button/bnum-icon-button';
import style from './bnum-input-search.less';
import {
	Autobind,
	Define,
	Fire,
	InitAttr,
	SetAttr,
} from '../../../core/decorators';
import { TAG_INPUT_SEARCH } from '../../../core/utils/constants/tags';
import { Render } from '../input/bnum-input.internal/template';
import {
	ATTRIBUTE_BUTTON,
	ATTRIBUTE_BUTTON_ICON,
	ATTRIBUTE_DISABLED,
	ATTRIBUTE_PLACEHOLDER,
	ATTRIBUTE_TYPE,
	ID_INPUT_BUTTON,
} from '../input/bnum-input.internal/constants';
import {
	BUTTON_ICON,
	EVENT_CLEAR,
	EVENT_SEARCH,
	ID_ACTIONS_CONTAINER,
	ID_CLEAR_BUTTON,
	INPUT_TYPE,
	SLOT_ACTIONS,
} from './bnum-input-search.internal/constants';
import { UI } from 'core/decorators/ui';
import { Listen } from 'core/decorators/listen';
import { Result, Risky } from '@rotomeca/rop';
import { BnumInputSearchCreateOptions } from './bnum-input-search.internal/types';
import { Listener } from 'core/decorators/listener';
import { JsCircularEvent } from '@rotomeca/event';
import { Nullable, Optional } from '@rotomeca/utils';
import { HTMLBnumButton } from '../button';

/**
 * Paramètres transmis lors du déclenchement de l'événement {@link HTMLBnumInputSearch.onclear}.
 *
 * @remarks
 * Permet aux abonnés d'intercepter et de modifier le comportement de vidage par défaut du composant.
 * En positionnant `ignoreOriginal` à `true`, l'abonné prend entièrement la main sur l'action de vidage.
 * La propriété `after` permet d'exécuter une fonction de rappel à l'issue du traitement par défaut,
 * sans nécessiter de le remplacer.
 *
 * @see {@link HTMLBnumInputSearch.onclear}
 */
type OnClearEventArgs = {
	/** Instance du composant ayant déclenché l'événement. */
	caller: HTMLBnumInputSearch;
	/**
	 * Si `true`, le comportement de vidage d'origine est ignoré.
	 * L'abonné est alors responsable de l'intégralité de l'action.
	 */
	ignoreOriginal: boolean;
	/** Référence en lecture seule à la fonction de rappel de changement de valeur de l'input. */
	inputValueChangedFunction: Readonly<(e: Event) => Result<void>>;
	/** Fonction optionnelle exécutée après le comportement de vidage par défaut. */
	after?: Nullable<() => void>;
};

/** Feuille de style CSS compilée spécifique au composant `bnum-input-search`. */
const SHEET = HTMLBnumInput.ConstructCSSStyleSheet(style);

//#region Template
/**
 * Fragment HTML du conteneur d'actions injecté dans le slot du composant parent.
 *
 * @remarks
 * Contient le bouton de vidage ({@link HTMLBnumButtonIcon}) et un slot nommé
 * `actions` permettant d'y injecter des contrôles personnalisés.
 *
 * @internal
 */
const TEMPLATE = (
	<div id={ID_ACTIONS_CONTAINER} part={ID_ACTIONS_CONTAINER}>
		<HTMLBnumButtonIcon id={ID_CLEAR_BUTTON}>close</HTMLBnumButtonIcon>
		<slot name={SLOT_ACTIONS}></slot>
	</div>
);
//#endregion Template
/**
 * Composant d'input de recherche.
 *
 * Utilise le composant de base `bnum-input` avec des configurations spécifiques pour la recherche.
 *
 * @category Input
 *
 * @structure Basique
 * <bnum-input-search>Label de recherche</bnum-input-search>
 *
 * @structure Avec une légende et un indice
 * <bnum-input-search>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input-search>
 *
 * @structure Désactivé
 * <bnum-input-search disabled placeholder="Recherche désactivée">
 *   Label du champ
 * </bnum-input-search>
 *
 * @structure Avec des boutons custom
 * <bnum-input-search placeholder="Recherche avec des boutons">
 *   Label du champ
 *   <bnum-icon-button slot="actions">filter_list</bnum-icon-button>
 *
 * </bnum-input-search>
 *
 * @slot button - Contenu du bouton de recherche (texte ou icône). (Inutilisé)
 * @slot actions - Contenu des actions personnalisées à droite du champ de recherche.
 *
 * @event {CustomEvent<{ value: string; name: string; caller: HTMLBnumInputSearch }>} bnum-input-search:search - Événement déclenché au clic par le bouton interne ou à la validation par la touche "Entrée". Envoie la valeur actuelle de l'input de recherche.
 * @event {MouseEvent} bnum-input:button.click - Événement déclenché au clic sur le bouton interne.
 * @event {CustomEvent<{ caller: HTMLBnumInputSearch }>} bnum-input-search:clear - Événement déclenché lors du clic sur le bouton de vidage du champ de recherche.
 *
 * @attr {string} (default: 'search') button-icon - Icône du bouton interne. Ne pas modifier, toujours 'search' pour ce composant.
 * @attr {string} (default: 'text') type - Type de l'input (text, password, email, etc.) Ne pas modifier, toujours 'text' pour ce composant.
 * @attr {undefined} (default: undefined) button - Attribut pour afficher le bouton interne. Ne pas modifier, toujours présent pour ce composant.
 */
// eslint-disable-next-line no-restricted-syntax
@Define({ template: Render(TEMPLATE), tag: TAG_INPUT_SEARCH })
export class HTMLBnumInputSearch extends HTMLBnumInput {
	//#region Private fields
	/**
	 * Références aux éléments du shadow DOM du composant.
	 *
	 * @remarks
	 * Contient le bouton de vidage (`emptyButton`) et le bouton de recherche (`searchButton`).
	 * Utilisé lorsque le composant est rendu en mode shadow DOM.
	 *
	 * @see {@link HTMLBnumInputSearch.#_lightUi}
	 * @internal
	 */
	@UI({
		emptyButton: `#${ID_ACTIONS_CONTAINER} ${HTMLBnumButtonIcon.TAG}`,
		searchButton: `#${ID_INPUT_BUTTON}`,
	})
	accessor #_ui!: {
		emptyButton: HTMLBnumButtonIcon;
		searchButton: HTMLBnumButton;
	};

	/**
	 * Références aux éléments du DOM léger (hors shadow root) du composant.
	 *
	 * @remarks
	 * Utilisé uniquement lorsque le composant n'est pas rendu comme un élément shadow.
	 *
	 * @see {@link HTMLBnumInputSearch.#_ui}
	 * @internal
	 */
	@UI({ searchButton: `#${ID_INPUT_BUTTON}` }, { shadowRoot: false })
	accessor #_lightUi!: { searchButton: HTMLBnumButton };

	/**
	 * Retourne le bouton de recherche actif selon le mode de rendu du composant.
	 *
	 * @remarks
	 * Délègue vers {@link HTMLBnumInputSearch.#_ui} en mode shadow DOM,
	 * ou vers {@link HTMLBnumInputSearch.#_lightUi} en mode light DOM.
	 *
	 * @returns Le bouton de recherche courant.
	 * @internal
	 */
	get #_searchButton() {
		return this._p_isShadowElement()
			? this.#_ui.searchButton
			: this.#_lightUi.searchButton;
	}
	//#endregion Private fields
	/**
	 * Événement déclenché lors du clic sur le bouton de vidage du champ de recherche.
	 */
	@Listener()
	accessor onclear!: JsCircularEvent<
		(params: OnClearEventArgs) => OnClearEventArgs
	>;

	//#region Lifecycle
	/**
	 * Constructeur du composant de recherche.
	 */
	constructor() {
		super();
	}

	/**
	 * Retourne les feuilles de style appliquées au composant,
	 * en ajoutant {@link SHEET} à celles héritées du composant parent.
	 *
	 * @returns Tableau de feuilles de style CSS à appliquer au shadow DOM.
	 * @internal
	 */
	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [...super._p_getStylesheets(), SHEET];
	}

	/**
	 * Précharge les attributs spécifiques à l'input de recherche.
	 * Définit le placeholder et l'icône du bouton si non présents.
	 */
	@SetAttr(ATTRIBUTE_BUTTON_ICON, 'search')
	@InitAttr(
		ATTRIBUTE_PLACEHOLDER,
		BnumConfig.Get('local_keys')?.search_field || 'Rechercher',
	)
	protected _p_preload(): void {}

	/**
	 * Construit le DOM du composant et attache le comportement du bouton de vidage.
	 *
	 * @remarks
	 * Si des abonnés sont enregistrés sur {@link HTMLBnumInputSearch.onclear}, ils sont invoqués
	 * en premier. Un abonné peut annuler le comportement par défaut via `ignoreOriginal`,
	 * ou planifier une action complémentaire via la propriété `after`.
	 *
	 * En l'absence d'interception, le comportement par défaut vide la valeur de l'input,
	 * déclenche le recalcul de l'état et émet l'événement `bnum-input-search:clear`.
	 *
	 * @internal
	 */
	protected _p_buildDOM(): void {
		super._p_buildDOM();

		this.#_ui.emptyButton!.addEventListener('click', () => {
			let after: Optional<Nullable<() => void>> = null;

			if (this.onclear.haveEvents()) {
				const params: OnClearEventArgs = this.onclear.call({
					caller: this,
					ignoreOriginal: false,
					after: null,
					inputValueChangedFunction: this._p_inputValueChangedCallback,
				});

				if (params?.ignoreOriginal) {
					if (params?.after) params.after?.();
					return;
				}

				if (params?.after) after = params.after;
			}

			this.value = EMPTY_STRING;
			this._p_inputValueChangedCallback(new Event('input'));
			this._triggerEventSearch();
			this.trigger(EVENT_CLEAR, { caller: this });

			if (after) after();
		});
	}

	/**
	 * Attache les événements nécessaires au composant.
	 * Supprime les attributs inutiles et gère les événements de recherche.
	 */
	protected _p_attach(): void {
		super._p_attach();

		this.removeAttribute(ATTRIBUTE_BUTTON);
		this.removeAttribute(ATTRIBUTE_BUTTON_ICON);

		this.onButtonClicked.add(EVENT_DEFAULT, this._triggerEventSearch);

		this.#_onKeyDown();
	}

	/**
	 * Gère le changement de valeur de l'input.
	 *
	 * @remarks
	 * Supprime temporairement puis restaure les attributs liés au bouton afin de
	 * préserver la configuration propre à l'input de recherche, quelle que soit
	 * la logique appliquée par la classe parente.
	 *
	 * @param e - Événement ayant déclenché le changement de valeur.
	 * @returns Résultat de l'opération de mise à jour.
	 * @internal
	 */
	@Autobind
	@Risky()
	protected _p_inputValueChangedCallback(e: Event): Result<void> {
		this.removeAttribute(ATTRIBUTE_BUTTON);
		this.setAttribute(ATTRIBUTE_BUTTON_ICON, BUTTON_ICON);

		const result = super._p_inputValueChangedCallback?.(e);

		this.removeAttribute(ATTRIBUTE_BUTTON);

		return result;
	}

	/**
	 * Nettoie les attributs après le rendu du composant.
	 */
	protected _p_postFlush(): void {
		this.removeAttribute(ATTRIBUTE_BUTTON);
		this.setAttribute(ATTRIBUTE_BUTTON_ICON, BUTTON_ICON);
		super._p_postFlush();
		this.removeAttribute(ATTRIBUTE_BUTTON);
	}
	//#endregion Lifecycle
	//#region Public Methods
	/**
	 * Désactive le bouton de recherche.
	 */
	disableSearchButton(): this {
		this.#_searchButton?.setAttribute?.(ATTRIBUTE_DISABLED, ATTRIBUTE_DISABLED);
		return this;
	}

	/**
	 * Active le bouton de recherche.
	 */
	enableSearchButton(): this {
		this.#_searchButton?.removeAttribute?.(ATTRIBUTE_DISABLED);
		return this;
	}

	/**
	 * Active l'état de chargement sur le composant.
	 *
	 * @remarks
	 * Désactive l'input et affiche l'indicateur de chargement sur le bouton de recherche.
	 * Appeler {@link HTMLBnumInputSearch.stopLoading} pour revenir à l'état initial.
	 *
	 * @returns L'instance courante pour le chaînage.
	 *
	 * @example
	 * const search = document.querySelector('bnum-input-search');
	 * search.setLoading();
	 * await fetchResults();
	 * search.stopLoading();
	 */
	setLoading() {
		return this.#_disable().#_setSearchButtonLoading();
	}

	/**
	 * Désactive l'état de chargement sur le composant.
	 *
	 * @remarks
	 * Réactive l'input et masque l'indicateur de chargement du bouton de recherche.
	 * À utiliser après {@link HTMLBnumInputSearch.setLoading}.
	 *
	 * @returns L'instance courante pour le chaînage.
	 *
	 * @example
	 * const search = document.querySelector('bnum-input-search');
	 * search.setLoading();
	 * await fetchResults();
	 * search.stopLoading();
	 */
	stopLoading() {
		return this.#_enable().#_unsetSearchButtonLoading();
	}
	//#endregion Public Methods
	//#region Private Methods
	/**
	 * Désactive le composant en posant l'attribut `disabled`.
	 *
	 * @returns L'instance courante pour le chaînage.
	 * @internal
	 */
	#_disable() {
		return this.attr('disabled', 'disabled');
	}

	/**
	 * Active l'indicateur de chargement sur le bouton de recherche.
	 *
	 * @returns L'instance courante pour le chaînage.
	 * @internal
	 */
	#_setSearchButtonLoading() {
		this.#_searchButton?.setLoading?.();
		return this;
	}

	/**
	 * Réactive le composant en retirant l'attribut `disabled`.
	 *
	 * @returns L'instance courante pour le chaînage.
	 * @internal
	 */
	#_enable() {
		this.removeAttribute('disabled');
		return this;
	}

	/**
	 * Désactive l'indicateur de chargement sur le bouton de recherche.
	 *
	 * @returns L'instance courante pour le chaînage.
	 * @internal
	 */
	#_unsetSearchButtonLoading() {
		this.#_searchButton?.stopLoading?.();
		return this;
	}

	/**
	 * Déclenche l'événement de recherche avec la valeur actuelle de l'input.
	 * @private
	 */
	@Autobind
	@Fire(EVENT_SEARCH)
	private _triggerEventSearch() {
		return {
			value: this.value,
			name: this.name,
			caller: this,
		};
	}

	/**
	 * Gestionnaire de l'événement `keydown` sur l'input.
	 *
	 * @remarks
	 * Bloque la propagation des frappes simples (sans modificateur `Ctrl`, `Alt` ou `Meta`)
	 * afin d'éviter les conflits avec les raccourcis clavier de l'application hôte.
	 * Déclenche la recherche à l'appui de la touche `Entrée`.
	 *
	 * @returns Fonction de rappel pour l'événement `keydown`.
	 * @internal
	 */
	@Listen('keydown')
	#_onKeyDown() {
		return (e: KeyboardEvent) => {
			if (!e.ctrlKey && !e.altKey && !e.metaKey) {
				e.stopPropagation();
			}

			if (e.key === 'Enter') {
				this._triggerEventSearch();
			}
		};
	}
	//#endregion Private Methods
	//#region Static Methods
	/**
	 * Retourne la liste des attributs observés, en excluant ceux spécifiques à la recherche.
	 * @inheritdoc
	 */
	protected static _p_observedAttributes(): string[] {
		return super._p_observedAttributes().filter(x => {
			switch (x) {
				case ATTRIBUTE_TYPE:
				case ATTRIBUTE_BUTTON:
				case ATTRIBUTE_BUTTON_ICON:
					return false;

				default:
					return true;
			}
		});
	}

	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns {HTMLBnumInput} Instance du composant.
	 */
	static Create(
		label: string,
		options: BnumInputSearchCreateOptions = {},
	): HTMLBnumInputSearch {
		const finalOptions = {
			type: INPUT_TYPE,
			...options,
		};
		return super.Create(label, finalOptions) as HTMLBnumInputSearch;
	}
	//#endregion Static Methods
}
